import { matchPath } from 'react-router-dom';

interface RouteEntry {
  route?: string;
  exact?: boolean;
}

/**
 * Which page a pathname belongs to, named by the route pattern that matches
 * it: `/flows/12/steps` and `/flows/12/settings` both answer
 * `/flows/:outreachId`.
 *
 * The silent upgrade reloads when this changes. It used to compare the raw
 * pathname, on the assumption that a pathname change meant the page was
 * being replaced anyway — which is false for any page that routes its own
 * tabs. `mco/details` renders `${match.path}/steps|settings|candidates`, so
 * every click between flow sections read as a page boundary and reloaded a
 * stale tab out from under whoever was configuring the flow.
 *
 * Matched in list order, so this agrees with the `<Switch>` in PrivateRoutes
 * that renders the same array: whichever route would render is the one named.
 *
 * Unmatched paths answer with themselves rather than a shared constant, so
 * two unregistered paths stay distinct instead of collapsing into one page.
 */
export const routeFamily = (
  pathname: string,
  routes: readonly RouteEntry[] = []
): string => {
  for (const entry of routes) {
    if (!entry?.route) continue;
    if (matchPath(pathname, { path: entry.route, exact: entry.exact ?? false }))
      return entry.route;
  }
  return pathname;
};
